import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  ApiError,
  getCustomersControllerFileQueryKey,
  getCustomersControllerUnitsQueryKey,
  useCustomersControllerCreateProperty,
  useCustomersControllerCreateUnit,
  useCustomersControllerFile,
  useCustomersControllerList,
  type UnitResponseDtoOutput,
} from '@werkbank/shared';
import { deriveUnitLabel } from '../lib/unitQuickCreate';
import { EditDialogFooter } from './MasterDataDialogs';
import { PlzVorschlag } from './PlzVorschlag';

/**
 * TICKET P5-C §3: Schnellanlage einer Mietereinheit ohne den Auftragsfluss zu
 * verlassen. Ein kompakter Dialog, den sowohl die Auftragsanlage (mit fixem
 * Kunden) als auch die globale Suche (Kunde wird hier gewählt) nutzen.
 *
 * Pflichtminimum bewusst klein (Mietvertragsnummer + Mietername); Objekt wählen
 * ODER neu anlegen (Straße/PLZ/Ort), Telefon/Stockwerk/Lage sind nachpflegbar.
 * Bei „neu" wird zuerst das Objekt, dann die Einheit angelegt; anschließend die
 * Einheiten- und Kundenakte-Query invalidiert und `onCreated` gemeldet, damit
 * der Aufrufer die frische Einheit sofort auswählen kann.
 */

const EMPTY_UNIT = {
  leaseNumber: '',
  tenantName: '',
  tenantPhone: '',
  floor: '',
  position: '',
};
const EMPTY_PROP = { street: '', zip: '', city: '' };

export interface UnitQuickCreateDialogProps {
  opened: boolean;
  onClose: () => void;
  /** Fixer Kunde (Auftragsanlage). Fehlt er, wählt der Dialog den Kunden. */
  customerId?: string;
  /** Vorbelegung der Mietvertragsnummer (Suche-Fehltreffer). */
  initialLeaseNumber?: string;
  /** Meldung nach erfolgreicher Anlage — Aufrufer selektiert die neue Einheit. */
  onCreated?: (unit: UnitResponseDtoOutput, customerId: string) => void;
}

export function UnitQuickCreateDialog({
  opened,
  onClose,
  customerId,
  initialLeaseNumber,
  onCreated,
}: UnitQuickCreateDialogProps) {
  const queryClient = useQueryClient();
  const [pickedCustomer, setPickedCustomer] = useState('');
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [propertyId, setPropertyId] = useState('');
  const [prop, setProp] = useState(EMPTY_PROP);
  const [unit, setUnit] = useState(EMPTY_UNIT);
  const [error, setError] = useState<string | null>(null);

  const effectiveCustomerId = customerId ?? pickedCustomer;

  // Kundenliste nur, wenn der Dialog selbst wählen muss (globale Suche).
  // Vollanalyse S5: Select ohne Suchverdrahtung → über der 200er-Kappung
  // anfordern (M19-Konvention, max. 1000).
  const customers = useCustomersControllerList(
    { archived: 'false', take: 1000 },
    { query: { enabled: opened && !customerId } },
  );
  // Objekte des Kunden aus der Kundenakte.
  const file = useCustomersControllerFile(effectiveCustomerId, undefined, {
    query: { enabled: opened && !!effectiveCustomerId },
  });
  const properties = file.data?.properties ?? [];
  // Ohne bestehende Objekte gibt es nur den Weg „neu anlegen".
  const hasProperties = properties.length > 0;
  const effectiveMode: 'existing' | 'new' = hasProperties ? mode : 'new';

  const createProperty = useCustomersControllerCreateProperty<ApiError>();
  const createUnit = useCustomersControllerCreateUnit<ApiError>();

  // Beim Öffnen Formular zurücksetzen und Vertragsnummer vorbelegen.
  useEffect(() => {
    if (opened) {
      setPickedCustomer('');
      setMode('existing');
      setPropertyId('');
      setProp(EMPTY_PROP);
      setUnit({ ...EMPTY_UNIT, leaseNumber: initialLeaseNumber?.trim() ?? '' });
      setError(null);
    }
  }, [opened, initialLeaseNumber]);

  const pending = createProperty.isPending || createUnit.isPending;

  // Pflichtminimum: Vertragsnummer + Name; dazu ein Ziel (Kunde) und ein Objekt
  // (gewähltes bestehendes ODER vollständige neue Adresse).
  const hasLease = unit.leaseNumber.trim().length > 0;
  const hasName = unit.tenantName.trim().length > 0;
  const hasCustomer = effectiveCustomerId.length > 0;
  const hasTarget =
    effectiveMode === 'existing'
      ? propertyId.length > 0
      : prop.street.trim().length > 0 && prop.zip.trim().length > 0 && prop.city.trim().length > 0;
  const canSubmit = hasLease && hasName && hasCustomer && hasTarget;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      let targetPropertyId = propertyId;
      if (effectiveMode === 'new') {
        const created = await createProperty.mutateAsync({
          id: effectiveCustomerId,
          data: {
            street: prop.street.trim(),
            zip: prop.zip.trim(),
            city: prop.city.trim(),
          },
        });
        targetPropertyId = created.id;
      }
      const newUnit = await createUnit.mutateAsync({
        id: targetPropertyId,
        data: {
          label: deriveUnitLabel({
            tenantName: unit.tenantName,
            floor: unit.floor,
            position: unit.position,
            leaseNumber: unit.leaseNumber,
          }),
          leaseNumber: unit.leaseNumber.trim() || undefined,
          floor: unit.floor.trim() || undefined,
          position: unit.position.trim() || undefined,
          tenantName: unit.tenantName.trim() || undefined,
          tenantPhone: unit.tenantPhone.trim() || undefined,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getCustomersControllerUnitsQueryKey(effectiveCustomerId),
      });
      await queryClient.invalidateQueries({
        queryKey: getCustomersControllerFileQueryKey(effectiveCustomerId),
      });
      onCreated?.(newUnit, effectiveCustomerId);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Anlage fehlgeschlagen.');
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Neue Einheit" size="md">
      <Stack gap="sm">
        {/* Kunde nur wählen, wenn kein fester Kunde vorgegeben ist. */}
        {!customerId && (
          <Select
            label="Kunde"
            required
            searchable
            placeholder="Kunde wählen"
            data={customers.data?.map((c) => ({ value: c.id, label: c.displayName })) ?? []}
            value={pickedCustomer || null}
            onChange={(v) => {
              setPickedCustomer(v ?? '');
              setPropertyId('');
              setMode('existing');
            }}
            data-testid="unit-quickcreate-customer"
          />
        )}

        {/* Objekt: bestehendes wählen ODER neu anlegen. Ohne Objekte nur „neu". */}
        {hasProperties && (
          <SegmentedControl
            fullWidth
            value={effectiveMode}
            onChange={(v) => setMode(v as 'existing' | 'new')}
            data={[
              { value: 'existing', label: 'Bestehendes Objekt' },
              { value: 'new', label: 'Neues Objekt' },
            ]}
            data-testid="unit-quickcreate-mode"
          />
        )}

        {effectiveMode === 'existing' ? (
          <Select
            label="Objekt"
            required
            searchable
            placeholder={hasCustomer ? 'Objekt wählen' : 'Zuerst Kunde wählen'}
            disabled={!hasCustomer}
            data={properties.map((p) => ({
              value: p.id,
              label: `${p.street}, ${p.zip} ${p.city}`,
            }))}
            value={propertyId || null}
            onChange={(v) => setPropertyId(v ?? '')}
            data-testid="unit-quickcreate-property"
          />
        ) : (
          <>
            <TextInput
              label="Straße"
              required
              value={prop.street}
              onChange={(e) => setProp({ ...prop, street: e.currentTarget.value })}
              data-testid="unit-quickcreate-street"
            />
            <Group grow>
              <TextInput
                label="PLZ"
                required
                value={prop.zip}
                onChange={(e) => setProp({ ...prop, zip: e.currentTarget.value })}
                data-testid="unit-quickcreate-zip"
              />
              <TextInput
                label="Ort"
                required
                value={prop.city}
                onChange={(e) => setProp({ ...prop, city: e.currentTarget.value })}
                data-testid="unit-quickcreate-city"
              />
            </Group>
            <PlzVorschlag
              street={prop.street}
              city={prop.city}
              zip={prop.zip}
              onUebernehmen={(v) => setProp({ ...prop, ...v })}
            />
          </>
        )}

        {/* Pflichtminimum: Vertragsnummer + Mietername. */}
        <TextInput
          label="Mietvertragsnummer"
          required
          placeholder="z. B. 72107.0009.03"
          value={unit.leaseNumber}
          onChange={(e) => setUnit({ ...unit, leaseNumber: e.currentTarget.value })}
          data-testid="unit-quickcreate-lease"
        />
        <TextInput
          label="Mietername"
          required
          value={unit.tenantName}
          onChange={(e) => setUnit({ ...unit, tenantName: e.currentTarget.value })}
          data-testid="unit-quickcreate-name"
        />
        <TextInput
          label="Telefon (optional)"
          value={unit.tenantPhone}
          onChange={(e) => setUnit({ ...unit, tenantPhone: e.currentTarget.value })}
          data-testid="unit-quickcreate-phone"
        />
        <Group grow>
          <TextInput
            label="Stockwerk (optional)"
            placeholder="z. B. 1. OG"
            value={unit.floor}
            onChange={(e) => setUnit({ ...unit, floor: e.currentTarget.value })}
            data-testid="unit-quickcreate-floor"
          />
          <TextInput
            label="Lage (optional)"
            placeholder="L / R"
            value={unit.position}
            onChange={(e) => setUnit({ ...unit, position: e.currentTarget.value })}
            data-testid="unit-quickcreate-position"
          />
        </Group>

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}
        <EditDialogFooter
          error={null}
          onCancel={onClose}
          onSave={() => void submit()}
          saving={pending}
          saveTestId="unit-quickcreate-submit"
          saveLabel="Einheit anlegen"
          saveDisabled={!canSubmit}
        />
      </Stack>
    </Modal>
  );
}
